/**
 * Calculator Logger Utility
 * Provides structured logging with performance metrics and change frequency tracking
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface StateChangeLog {
  field: string;
  oldValue: any;
  newValue: any;
  timestamp: number;
  source: string;
}

export interface PerformanceLog {
  operation: string;
  durationMs: number;
  timestamp: number;
}

export interface CalculatorMetrics {
  totalChanges: number;
  changesByField: Record<string, number>;
  avgCalculationTime: number;
  peakChangesPerSecond: number;
  sessionDurationMs: number;
  circuitBreakerActivations: number;
  lastActivity: number;
}

const MAX_LOG_ENTRIES = 100;
const FREQUENCY_WINDOW_MS = 1000; // 1 second window for frequency tracking

class CalculatorLoggerClass {
  private stateChanges: StateChangeLog[] = [];
  private performanceLogs: PerformanceLog[] = [];
  private changesByField: Record<string, number> = {};
  private sessionStartTime: number = Date.now();
  private circuitBreakerActivations: number = 0;
  private recentChangeTimes: number[] = [];
  private peakChangesPerSecond: number = 0;
  private isProduction: boolean = import.meta.env.PROD;
  private subscribers: Set<() => void> = new Set();

  /**
   * Subscribe to logger updates
   */
  subscribe(callback: () => void): () => void {
    this.subscribers.add(callback);
    return () => {
      this.subscribers.delete(callback);
    };
  }

  /**
   * Notify all subscribers of changes
   */
  private notifySubscribers(): void {
    this.subscribers.forEach(cb => cb());
  }

  /**
   * Log a state change event
   */
  logStateChange(field: string, oldValue: any, newValue: any, source: string = 'unknown'): void {
    const now = Date.now();
    
    const log: StateChangeLog = {
      field,
      oldValue,
      newValue,
      timestamp: now,
      source,
    };

    // Add to history (circular buffer)
    this.stateChanges.push(log);
    if (this.stateChanges.length > MAX_LOG_ENTRIES) {
      this.stateChanges.shift();
    }

    // Track by field
    this.changesByField[field] = (this.changesByField[field] || 0) + 1;

    // Update frequency tracking
    this.updateFrequencyMetrics(now);

    // Console output (only in dev)
    if (!this.isProduction) {
      console.log(
        `%c[Calculator] ${field}`,
        'color: #3b82f6; font-weight: bold',
        { from: oldValue, to: newValue, source }
      );
    }

    // Notify subscribers
    this.notifySubscribers();
  }

  /**
   * Log a performance measurement
   */
  logPerformance(operation: string, durationMs: number): void {
    const log: PerformanceLog = {
      operation,
      durationMs,
      timestamp: Date.now(),
    };

    this.performanceLogs.push(log);
    if (this.performanceLogs.length > MAX_LOG_ENTRIES) {
      this.performanceLogs.shift();
    }

    // Warn if operation is slow
    if (durationMs > 100) {
      console.warn(`[Calculator] Slow operation: ${operation} took ${durationMs.toFixed(2)}ms`);
    } else if (!this.isProduction) {
      console.log(
        `%c[Calculator] ${operation}`,
        'color: #22c55e',
        `${durationMs.toFixed(2)}ms`
      );
    }

    this.notifySubscribers();
  }

  /**
   * Log circuit breaker activation
   */
  logCircuitBreaker(field: string, reason: string): void {
    this.circuitBreakerActivations++;
    console.warn(`[Calculator] Circuit breaker activated for ${field}: ${reason}`);
    this.notifySubscribers();
  }

  /**
   * Log hydration events
   */
  logHydration(source: string, fieldCount: number, durationMs: number): void {
    if (!this.isProduction) {
      console.log(
        `%c[Calculator] Hydration from ${source}`,
        'color: #f59e0b; font-weight: bold',
        { fields: fieldCount, duration: `${durationMs.toFixed(2)}ms` }
      );
    }
    this.logPerformance(`hydration:${source}`, durationMs);
  }

  /**
   * Log save events
   */
  logSave(destination: string, fieldCount: number, durationMs: number): void {
    if (!this.isProduction) {
      console.log(
        `%c[Calculator] Save to ${destination}`,
        'color: #8b5cf6',
        { fields: fieldCount, duration: `${durationMs.toFixed(2)}ms` }
      );
    }
    this.logPerformance(`save:${destination}`, durationMs);
  }

  /**
   * Update change frequency metrics
   */
  private updateFrequencyMetrics(now: number): void {
    // Add current timestamp
    this.recentChangeTimes.push(now);
    
    // Remove timestamps outside the window
    this.recentChangeTimes = this.recentChangeTimes.filter(
      t => now - t < FREQUENCY_WINDOW_MS
    );
    
    // Update peak if current is higher
    const currentRate = this.recentChangeTimes.length;
    if (currentRate > this.peakChangesPerSecond) {
      this.peakChangesPerSecond = currentRate;
    }

    // Warn if rate is suspiciously high (possible loop)
    if (currentRate > 5) {
      console.warn(
        `[Calculator] High change frequency detected: ${currentRate} changes/second`,
        { recentFields: this.stateChanges.slice(-5).map(c => c.field) }
      );
    }
  }

  /**
   * Get aggregated metrics
   */
  getMetrics(): CalculatorMetrics {
    const totalCalculationTime = this.performanceLogs
      .filter(p => p.operation.startsWith('calculation'))
      .reduce((sum, p) => sum + p.durationMs, 0);
    
    const calculationCount = this.performanceLogs
      .filter(p => p.operation.startsWith('calculation')).length;

    return {
      totalChanges: this.stateChanges.length,
      changesByField: { ...this.changesByField },
      avgCalculationTime: calculationCount > 0 ? totalCalculationTime / calculationCount : 0,
      peakChangesPerSecond: this.peakChangesPerSecond,
      sessionDurationMs: Date.now() - this.sessionStartTime,
      circuitBreakerActivations: this.circuitBreakerActivations,
      lastActivity: this.stateChanges.length > 0 
        ? this.stateChanges[this.stateChanges.length - 1].timestamp 
        : this.sessionStartTime,
    };
  }

  /**
   * Get recent state changes
   */
  getRecentChanges(count: number = 10): StateChangeLog[] {
    return this.stateChanges.slice(-count);
  }

  /**
   * Get performance logs
   */
  getPerformanceLogs(count: number = 20): PerformanceLog[] {
    return this.performanceLogs.slice(-count);
  }

  /**
   * Clear all logs and reset metrics
   */
  clear(): void {
    this.stateChanges = [];
    this.performanceLogs = [];
    this.changesByField = {};
    this.circuitBreakerActivations = 0;
    this.recentChangeTimes = [];
    this.peakChangesPerSecond = 0;
    this.sessionStartTime = Date.now();
    this.notifySubscribers();
  }
}

// Export singleton instance
export const calculatorLogger = new CalculatorLoggerClass();
